import React, { useState, useEffect } from "react";
import { Modal } from "react-bootstrap";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import For from "../../images/about/log_art.jpg";
import { Eye, EyeOff } from "lucide-react";
import { Country, State, City } from "country-state-city";
import OtpModel from "./OtpModel";
import { useAuth } from "../../contexts/AuthContext";
import toast from 'react-hot-toast';

const getValidationSchema = (signUpType) =>
  Yup.object().shape({
    full_name: Yup.string().required("Full Name is required"),
    email: Yup.string().email("Invalid email").required("Email is required"),
    occupation: Yup.string().required("Occupation is required"),
    dob: Yup.string().required("Date of Birth is required"),
    phone: Yup.string()
      .matches(/^[0-9]{10}$/, "Phone number must be 10 digits")
      .required("Phone number is required"),
    password: Yup.string()
      .min(6, "Password must be at least 6 characters")
      .required("Password is required"),
    confirmPassword: Yup.string()
      .oneOf([Yup.ref("password"), null], "Passwords must match")
      .required("Confirm Password is required"),
    company_name:
      signUpType === "company"
        ? Yup.string().required("Company Name is required")
        : Yup.string(),
    registeration_id:
        signUpType === "company"
          ? Yup.string().required("Company Name is required")
          : Yup.string(),
    country: Yup.string().required("Country is required"),
    state: Yup.string().required("State is required"),
    city: Yup.string().required("City is required"),
    gender: Yup.string().required("Gender is required"),
    profile_type: Yup.string().required("Profile Type is required"),
    profile_pic: Yup.mixed()
      .required("Profile Picture is required")
      .test("fileSize", "File too large", (value) => value && value.size <= 2000000)
      .test("fileFormat", "Unsupported Format", (value) =>
        value && ["image/jpeg", "image/png", "image/jpg"].includes(value.type)
      ),
  });

function SignUpModal({ show, onClose, selectedType, onSignUpSuccess }) {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [signUpType, setSignUpType] = useState(selectedType || 'individual');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { login } = useAuth();
  const [selectedCountry, setSelectedCountry] = useState("");
  const [states, setStates] = useState([]);
  const [selectedState, setSelectedState] = useState("");
  const [cities, setCities] = useState([]);
  const [showOtp, setShowOtp] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState("");

  const handleCountryChange = (e, setFieldValue) => {
    const countryCode = e.target.value;
    setSelectedCountry(countryCode);
    setFieldValue("country", countryCode);
    const statesList = State.getStatesOfCountry(countryCode);
    setStates(statesList || []);
    setCities([]);
    setSelectedState("");
    setFieldValue("state", "");
    setFieldValue("city", "");
  };

  const handleStateChange = (e, setFieldValue) => {
    const stateCode = e.target.value;
    setSelectedState(stateCode);
    setFieldValue("state", stateCode);
    const citiesList = City.getCitiesOfState(selectedCountry, stateCode);
    setCities(citiesList || []);
    setFieldValue("city", "");
  };

  useEffect(() => {
    setSignUpType(selectedType);
  }, [selectedType]);

  return (
   <>
    <Modal
      show={show}
      onHide={onClose}
      centered
      dialogClassName="signup-modal-full w-100 custom-modal"
      size="xl"
    >
      <div className="d-flex w-100">
        <div className="w-50 d-none d-md-block">
          <img src={For} alt="log_banner" className="h-100 w-100 object-fit-cover" />
        </div>
        <div className="w-100 w-md-50 p-4 bg-white">
          <h3 className="mb-4">Sign Up - {signUpType === "individual" ? "Individual" : "Company"}</h3>

          {/* Tab Switcher */}
          <div className="tab-slider-container d-flex justify-content-start mb-4 position-relative">
            <div className="tab-slider bg-white rounded-pill px-1 py-1 position-relative d-inline-flex">
              <div
                className="tab-highlight position-absolute rounded-pill"
                style={{
                  transition: "transform 0.3s ease, width 0.3s ease",
                  height: "100%",
                  width: "50%",
                  top: 0,
                  left: 0,
                  transform: signUpType === "company" ? "translateX(100%)" : "translateX(0%)",
                  zIndex: 1,
                }}
              ></div>
              <button
                className={`btn position-relative tab-btn px-4 py-2 ${signUpType === "individual" ? "text-white" : "text-dark"}`}
                style={{ zIndex: 2 }}
                onClick={() => setSignUpType("individual")}
              >
                Individual
              </button>
              <button
                className={`btn position-relative tab-btn px-4 py-2 ${signUpType === "company" ? "text-white" : "text-dark"}`}
                style={{ zIndex: 2 }}
                onClick={() => setSignUpType("company")}
              >
                Company
              </button>
            </div>
          </div>

          <Formik
            initialValues={{
              full_name: "",
              email: "",
              phone: "",
              occupation: "",
              dob: "",
              country: "",
              state: "",
              city: "",
              password: "",
              confirmPassword: "",
              company_name: "",
              signUpType: signUpType,
              gender: "",
              profile_type: "",
              registeration_id:"",
              profile_pic: null,
              status:"allow_check"
            }}
            validationSchema={getValidationSchema(signUpType)}
            onSubmit={async (values, { setSubmitting, resetForm, setFieldError }) => {
              setIsSubmitting(true);
              try {
                const formData = new FormData();
                Object.keys(values).forEach((key) => {
                  if (key === 'profile_pic' && values[key]) {
                    formData.append('profile_pic', values[key]);
                  } else if (values[key] !== undefined && values[key] !== null) {
                    formData.append(key, values[key]);
                  }
                });

                const base_url = process.env.REACT_APP_BASE_URL;
                const response = await fetch(`${base_url}/api/user/userRegistration`, {
                  method: 'POST',
                  body: formData,
                });

                const data = await response.json();

                if (response.ok) {
                  toast.success('Registration successful! Please verify your email.');
                  if (onSignUpSuccess) {
                    onSignUpSuccess();
                  }
                  resetForm();
                  onClose();
                } else {
                  if (data.errors) {
                    Object.entries(data.errors).forEach(([field, message]) => {
                      setFieldError(field, message);
                    });
                  }
                  toast.error(data.message || 'Registration failed. Please check your details.');
                }
              } catch (error) {
                console.error('Registration error:', error);
                toast.error('An error occurred during registration');
                console.error("Error submitting form:", error);
                toast.success("Error submitting form");
                toast.error("Something went wrong. Please try again.");
              } finally {
                setSubmitting(false);
              }
            }}
            enableReinitialize
          >
            {({ setFieldValue, values }) => (
              <Form style={{ maxHeight: "65vh", overflowY: "auto" }}>
                <Field type="hidden" name="signUpType" />

                {signUpType === "company" && (
                  <>
                  <div className="row">
                  <div className="col-md-6 mb-3">
                    <label className="mb-2">Registration Number</label>
                    <Field name="registeration_id" type="text" className="form-control" />
                    <ErrorMessage name="registeration_id" component="div" className="text-danger" />
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="mb-2">Company Name</label>
                    <Field name="company_name" type="text" className="form-control" />
                    <ErrorMessage name="company_name" component="div" className="text-danger" />
                  </div>

                  </div>
                  </>
                  
                )}

                <div className="row">
                  <div className="col-md-6 mb-3">
                    <label className="mb-2">Full Name</label>
                    <Field name="full_name" type="text" className="form-control" />
                    <ErrorMessage name="full_name" component="div" className="text-danger" />
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="mb-2">Email</label>
                    <Field name="email" type="email" className="form-control" />
                    <ErrorMessage name="email" component="div" className="text-danger" />
                  </div>
                </div>

                <div className="row">
                  <div className="col-md-6 mb-3">
                    <label className="mb-2">Occupation</label>
                    <Field name="occupation" type="text" className="form-control" />
                    <ErrorMessage name="occupation" component="div" className="text-danger" />
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="mb-2">Phone</label>
                    <Field name="phone" type="tel" className="form-control" />
                    <ErrorMessage name="phone" component="div" className="text-danger" />
                  </div>
                </div>

                <div className="row">
                  <div className="col-md-6 mb-3">
                    <label className="mb-2">Date Of Birth</label>
                    <Field name="dob" type="date" className="form-control" />
                    <ErrorMessage name="dob" component="div" className="text-danger" />
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="mb-2">Country</label>
                    <Field
                      as="select"
                      name="country"
                      className="form-control"
                      onChange={(e) => handleCountryChange(e, setFieldValue)}
                    >
                      <option value="">Select Country</option>
                      {Country.getAllCountries().map((country) => (
                        <option key={country.isoCode} value={country.isoCode}>
                          {country.name}
                        </option>
                      ))}
                    </Field>
                    <ErrorMessage name="country" component="div" className="text-danger" />
                  </div>
                </div>

                <div className="row">
                  <div className="col-md-6 mb-3">
                    <label className="mb-2">State</label>
                    <Field
                      as="select"
                      name="state"
                      className="form-control"
                      onChange={(e) => handleStateChange(e, setFieldValue)}
                      disabled={!selectedCountry}
                    >
                      <option value="">Select State</option>
                      {states.map((state) => (
                        <option key={state.isoCode} value={state.isoCode}>
                          {state.name}
                        </option>
                      ))}
                    </Field>
                    <ErrorMessage name="state" component="div" className="text-danger" />
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="mb-2">City</label>
                    <Field as="select" name="city" className="form-control" disabled={!selectedState}>
                      <option value="">Select City</option>
                      {cities.map((city) => (
                        <option key={city.name} value={city.name}>
                          {city.name}
                        </option>
                      ))}
                    </Field>
                    <ErrorMessage name="city" component="div" className="text-danger" />
                  </div>
                </div>

                <div className="row">
                  <div className="col-md-6 mb-3">
                    <label className="mb-2">Gender</label>
                    <Field as="select" name="gender" className="form-control">
                      <option value="">Select Gender</option>
                      <option value="male">Male</option>
                      <option value="female">Female</option>
                      <option value="other">Other</option>
                    </Field>
                    <ErrorMessage name="gender" component="div" className="text-danger" />
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="mb-2">Profile Type</label>
                    <Field as="select" name="profile_type" className="form-control">
                      <option value="">Select Profile Type</option>
                      <option value="individual">Individual</option>
                      <option value="company">Company</option>
                    </Field>
                    <ErrorMessage name="profile_type" component="div" className="text-danger" />
                  </div>
                </div>

                <div className="mb-3">
                  <label className="mb-2">Profile Picture</label>
                  <input
                    type="file"
                    accept="image/*"
                    className="form-control"
                    onChange={(event) => setFieldValue("profile_pic", event.currentTarget.files[0])}
                  />
                  <ErrorMessage name="profile_pic" component="div" className="text-danger" />
                  {values.profile_pic && (
                    <div className="mt-2">
                      <img
                        src={URL.createObjectURL(values.profile_pic)}
                        alt="Preview"
                        className="img-thumbnail"
                        width={120}
                      />
                    </div>
                  )}
                </div>

                <div className="row">
                  <div className="col-md-6 mb-3 position-relative">
                    <label className="mb-2">Password</label>
                    <Field
                      name="password"
                      type={showPassword ? "text" : "password"}
                      className="form-control pe-5"
                    />
                    <span
                      onClick={() => setShowPassword(!showPassword)}
                      style={{ position: "absolute", top: "38px", right: "30px", cursor: "pointer" }}
                    >
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </span>
                    <ErrorMessage name="password" component="div" className="text-danger" />
                  </div>
                  <div className="col-md-6 mb-3 position-relative">
                    <label className="mb-2">Confirm Password</label>
                    <Field
                      name="confirmPassword"
                      type={showConfirmPassword ? "text" : "password"}
                      className="form-control pe-5"
                    />
                    <span
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      style={{ position: "absolute", top: "38px", right: "30px", cursor: "pointer" }}
                    >
                      {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </span>
                    <ErrorMessage name="confirmPassword" component="div" className="text-danger" />
                  </div>
                </div>

               {/* Submit Buttons */}
               <div className="text-end">
                  <button
                    type="button"
                    onClick={onClose}
                    className="btn login-btn text-white position-relative overflow-hidden"
                    style={{ fontSize: "18px", fontFamily: "inherit" }}
                  >
                    <div className="swift-btn_animation d-flex justify-content-center align-items-center log_cus">
                      <p className="m-0 text-black">Close</p>
                    </div>
                  </button>
                  <button type="submit" className="btn signup-btn text-white position-relative overflow-hidden border-0">
                    <div
                      className="swift-btn_animation d-flex justify-content-center align-items-center"
                      style={{ background: "radial-gradient(circle,rgb(0, 0, 0),rgb(0, 0, 0))" }}
                    >
                      <p className="m-0 text-black fw-bold button_name">Register</p>
                    </div>
                  </button>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </div>
    </Modal>
    {showOtp && (
  <OtpModel
    show={showOtp}
    onClose={() => {
      setShowOtp(false);
      onClose();
    }}
    email={registeredEmail} 
  />
)}

   </>
    
  );
}

export default SignUpModal;
